/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import LocalPhoneIcon from '@material-ui/icons/LocalPhone';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
    shape, object, arrayOf, func, bool,
} from 'prop-types';
import ReactMarkdown from 'react-markdown';
import mbpLogger from '@module-logger/mbp';
import { setChatbotOpenState } from '../../../../../state/ducks/App/App-Actions';
import { actions as tagManagerActions } from '../../../../../state/ducks/TagManager';
import customBreakpoints from '../../../../helpers/customBreakpoints';
import toggleWebChat from '../../../../helpers/toggleWebChat';

const styles = (theme) => ({
    contactUsContainer: {
        margin: '30px 0',
        width: '100%',
        background: theme.palette.csBgColor,
    },
    phone: {
        textAlign: 'left',
        marginBottom: '15px',
        marginLeft: '15px',
        '& p': {
            fontSize: '14px',
            padding: 0,
            margin: 0,
        },
        '&:last-child': {
            marginBottom: '0',
        },
    },
    contactUsHeader: {
        display: 'flex',
        padding: '10px',
        alignItems: 'center',
        fontWeight: '600',
        fontSize: '14px',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            textAlign: 'center',
        },
    },
    contactUs: {
        minHeight: '50px',
        cursor: 'pointer',
        margin: '10px 5px',
        display: 'flex',
        fontSize: '16px',
        background: theme.palette.common?.white,
        textAlign: 'center',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey8}`,
        padding: '7px 35px',
        '& svg': {
            fill: theme.palette.cms?.primary || theme.palette.colorPrimary,
        },
    },
    contactUsInner: {
        width: '75%',
        margin: '0 auto',
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '100%',
        },
    },
    buttonWrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0px 10px',
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'center',
        },
    },
    chatBotButton: {
        minHeight: '50px',
        cursor: 'pointer',
        margin: '10px 5px',
        display: 'flex',
        fontSize: '16px',
        background: theme.palette.common?.white,
        textAlign: 'center',
        alignItems: 'center',
        border: `1px solid ${theme.palette.grey8}`,
        padding: '7px 20px',
        '& svg': {
            fill: theme.palette.cms?.primary || theme.palette.colorPrimary,
            marginRight: '5px',
            '& g': {
                fill: theme.palette.cms?.primary || theme.palette.colorPrimary,
            },
        },
    },
    hidden: {
        display: 'none',
    },
});

const ContactUsBannerBuilder = ({
    classes, data, trackEvent, enableChatBot, featureFlags,
}) => {
    const [showPhoneContact, setShowPhoneContact] = useState(false);
    const isShowingCallUsButton = featureFlags['is-cs-page-showing-call-us-button'];
    const contactData = data?.find((dataList) => dataList?.contact_us)?.contact_us;
    if (!contactData) {
        mbpLogger.logError({
            message: `No contactus data returned from CMS \n contactUsData = ${contactData}`,
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
        });
    }
    return (
        <div className={featureFlags['is-webchat-enabled'] || isShowingCallUsButton ? classes.contactUsContainer : ''}>
            <div className={classes.contactUsInner}>
                <Grid container>
                    {isShowingCallUsButton && <Grid item xs={12} sm={12} md={6} lg={6} className={classes.contactUsHeader}>{contactData?.header}</Grid>}
                    <Grid className={classes.buttonWrapper} item xs={12} sm={12} md={6} lg={6}>
                        {isShowingCallUsButton && (
                            <button id="callUs" type="button" className={classes.contactUs} onClick={() => { setShowPhoneContact(!showPhoneContact); }}>
                                <LocalPhoneIcon />
                                Call Us
                            </button>
                        )}
                        {!contactData?.hide_chat_btn && featureFlags['is-webchat-enabled'] && (
                            <button
                                id="livechat"
                                type="button"
                                className={classes.chatBotButton}
                                onClick={() => {
                                    trackEvent({
                                        eventCategory: 'Customer Service Hub', eventAction: 'Chat', eventLabel: 'path Ex: /customer-service',
                                    });
                                    enableChatBot(true);
                                    toggleWebChat();
                                }}
                            >
                                <svg className="MuiSvgIcon-root" focusable="false" viewBox="0 0 29 25" aria-hidden="true" role="presentation"><g id="02-Mocks" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd"><g id="01a---Help-Center---Home---Banner---COVID" transform="translate(-2560.000000, -3062.000000)"><g id="Contact-Us-Bar" transform="translate(1.000000, 3015.000000)"><g id="Group-5" transform="translate(2522.000000, 26.000000)"><g id="Group-3" transform="translate(37.000000, 17.000000)"><g id="add_alert-copy" transform="translate(0.000000, 4.000000)"><mask id="mask-3" fill="white"><path d="M14.5,0.104166605 C6.67513017,0.104166605 0.333333262,5.25618486 0.333333262,11.6145833 C0.333333262,14.359375 1.51757806,16.8717448 3.48763015,18.8473308 C2.79589838,21.6363933 0.482747326,24.1210938 0.455078055,24.1487631 C0.333333262,24.2760417 0.300130137,24.4641928 0.372070242,24.6302084 C0.444010346,24.796224 0.598958264,24.8958334 0.776041598,24.8958334 C4.44498693,24.8958334 7.19531246,23.1360678 8.5566406,22.0514323 C10.3662109,22.7320964 12.375,23.1250001 14.5,23.1250001 C22.3248698,23.1250001 28.6666667,17.9729818 28.6666667,11.6145833 C28.6666667,5.25618486 22.3248698,0.104166605 14.5,0.104166605 Z M7.41666663,13.3854167 C6.43717444,13.3854167 5.64583329,12.5940755 5.64583329,11.6145833 C5.64583329,10.6350911 6.43717444,9.84374999 7.41666663,9.84374999 C8.39615882,9.84374999 9.18749997,10.6350911 9.18749997,11.6145833 C9.18749997,12.5940755 8.39615882,13.3854167 7.41666663,13.3854167 Z M14.5,13.3854167 C13.5205078,13.3854167 12.7291667,12.5940755 12.7291667,11.6145833 C12.7291667,10.6350911 13.5205078,9.84374999 14.5,9.84374999 C15.4794922,9.84374999 16.2708333,10.6350911 16.2708333,11.6145833 C16.2708333,12.5940755 15.4794922,13.3854167 14.5,13.3854167 Z M21.5833334,13.3854167 C20.6038412,13.3854167 19.8125,12.5940755 19.8125,11.6145833 C19.8125,10.6350911 20.6038412,9.84374999 21.5833334,9.84374999 C22.5628256,9.84374999 23.3541667,10.6350911 23.3541667,11.6145833 C23.3541667,12.5940755 22.5628256,13.3854167 21.5833334,13.3854167 Z" id="path-1" /></mask><g id="comment-dots" fillRule="nonzero" /><g id="🎨-color" mask="url(#mask-3)" fill="#532380"><g transform="translate(-2.500000, -4.500000)" id="🎨-Color"><rect x="0" y="0" width="34" height="34" /></g></g></g></g></g></g></g></g></svg>
                                Start Live Chat
                            </button>
                        )}
                        {isShowingCallUsButton && showPhoneContact  && (
                            <div style={{ display: showPhoneContact ? 'block' : 'none', marginBottom: '30px', width: '100%' }}>
                                <ReactMarkdown
                                    source={contactData?.call_copy_english}
                                    skipHtml
                                    className={classes.phone}
                                />
                                <ReactMarkdown
                                    source={contactData?.call_copy_spanish}
                                    skipHtml
                                    className={classes.phone}
                                />
                            </div>
                        )}
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

ContactUsBannerBuilder.propTypes = {
    classes: object.isRequired,
    data: arrayOf(shape({
        modular_block: object,
    }).isRequired).isRequired,
    trackEvent: func.isRequired,
    enableChatBot: func.isRequired,
    featureFlags: shape({
        'is-webchat-enabled': bool,
    }),
};

ContactUsBannerBuilder.defaultProps = {
    featureFlags: {},
};
const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(tagManagerActions.trackEvent, dispatch),
    enableChatBot: bindActionCreators(setChatbotOpenState, dispatch),
});

export default connect(null, mapDispatchToProps)(withStyles(styles)(ContactUsBannerBuilder));
